import React from "react";
import { StarIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { Card, Button, Badge, cn } from "../ui";

import {
  useFavourites,
  addFavourite,
  deleteFavourite,
  checkIsFavourite,
} from "../data/favourites";
import { useOrder, addLineItem } from "../data/order";
import { useMixture } from "../data/mixture";

import AuthService from "../services/authentication";

function ProductCard(props) {
  const product = props.product;
  // Ticket 25660 — dispensary-flagged long-term OOS herb. Practitioners can see it
  // but must not be able to add it to a mixture.
  const isUnavailable = Boolean(product.temporarily_unavailable);
  let auth = AuthService.useAuth();
  const { favouriteProducts, mutate } = useFavourites(auth.authHeader());
  const isFavourite = checkIsFavourite(product.id, favouriteProducts);
  const { mutate: mutateOrder } = useOrder(auth.authHeader());
  const { addLineItem: addLineItemToMixture } = useMixture(
    props.mixtureId,
    auth.authHeader()
  );

  const onAddToOrder = async () => {
    if (isUnavailable) return;
    if (props.for_mixture) {
      addLineItemToMixture(product.id);
    } else {
      await addLineItem({
        product_id: product.id,
        auth_header: auth.authHeader(),
      });
      mutateOrder();
    }
  };

  return (
    <Card className={cn("h-full flex flex-col", product.mixture && "border-gray-800")}>
      <Card.Img className="p-2" src={product.image_url} />
      <Card.Body className="grow flex flex-col">
        <div className="justify-between flex flex-col h-full">
          <div>
            <h6 className="text-gray-500 font-light">{product.brand}</h6>
            <Card.Title>{product.name}</Card.Title>
          </div>
          <div className="pt-2">
            {isUnavailable && (
              <Card.Text className="mb-2">
                <Badge bg="danger">Temporarily unavailable</Badge>
              </Card.Text>
            )}
            {product.price && (
              <Card.Text className="mb-0 font-light mt-2 flex justify-between">
                <span className="text-gray-500">Unit Price:</span>
                <span>
                  ${product.price.toFixed(2)}
                  {props.for_mixture && "/ml"}
                </span>
              </Card.Text>
            )}
            {product.rrp && (
              <Card.Text className="mb-0 font-light flex justify-between">
                <span className="text-gray-500">RRP:</span>
                <span>
                  ${product.rrp.toFixed(2)}
                  {props.for_mixture && "/ml"}
                </span>
              </Card.Text>
            )}
            {product.available_stock !== null &&
            product.available_stock !== undefined &&
            !product.mixture &&
            !props.for_mixture ? (
              product.available_stock > 0 ? (
                <Card.Text className="flex font-light justify-between">
                  <span className="text-gray-500">Stock:</span>
                  <span>
                    {product.available_stock}
                    {props.for_mixture && "ml"}
                  </span>
                </Card.Text>
              ) : (
                <Card.Text className="flex font-light justify-between">
                  <span className="text-gray-500">Stock:</span>
                  <span>Out of stock</span>
                </Card.Text>
              )
            ) : (
              ""
            )}
            <div className="flex items-center justify-between mt-3 mb-2">
              <button
                type="button"
                onClick={() => props.onShowModalClick(product)}
                className="text-sm font-medium text-integria-green hover:underline"
              >
                View details
              </button>
              {!product.mixture && (
                <button
                  type="button"
                  title={isFavourite ? "Remove from favourites" : "Add to favourites"}
                  aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
                  onClick={() => {
                    const toggle = isFavourite ? deleteFavourite : addFavourite;
                    toggle({
                      product_id: product.id,
                      auth_header: auth.authHeader(),
                    }).then(() => mutate());
                  }}
                  className={cn(
                    "hover:text-integria-yellow",
                    isFavourite ? "text-integria-yellow" : "text-gray-400"
                  )}
                >
                  {isFavourite ? (
                    <StarIconSolid className="h-6 w-6" />
                  ) : (
                    <StarIcon className="h-6 w-6" />
                  )}
                </button>
              )}
            </div>
            <Button
              className="w-full font-bold"
              variant="secondary"
              onClick={onAddToOrder}
              disabled={isUnavailable}
            >
              Select
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default ProductCard;
